import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  completeVoiceUpload,
  getListVoicesQueryKey,
  prepareVoiceUpload,
} from "../../api/generated/endpoints/generation/generation";
import type { Voice } from "../../api/generated/model";
import { refreshTeamStorage } from "../../api/team-storage";
import { sendToBucket } from "../../api/upload";
import { toUserMessage } from "../messages";
import { voiceMimeOf } from "./limits";

export interface VoiceSave {
  /** Saves the file as the brand's voice under `name`; true once it is one. */
  save: (file: File, name: string) => Promise<boolean>;
  pending: boolean;
  /** 0 to 1 while the file goes up (live's ring), else null. */
  progress: number | null;
  /** Live's words when it failed: "<typed name>: <why>". */
  problem: string | null;
}

/**
 * A recorded or uploaded file made the brand's voice (live's `useSaveVoice`, G7-team-voices):
 * the name with its file's extension, as live sends it; the server's upload form; the file to
 * the bucket through the one upload path; then the complete, which reads its length. The team's
 * voice lists are read again, and `onSaved` gets the voice. Closing the panel mid-upload lets it
 * finish, as live's does.
 */
export function useVoiceSave(
  teamId: string,
  brandId: string,
  onSaved: (voice: Voice) => void,
): VoiceSave {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: async ({ file, name }: { file: File; name: string }) => {
      const mime = voiceMimeOf(file) ?? "audio/wav";
      const ticket = await prepareVoiceUpload(teamId, brandId, { name, mime, bytes: file.size });
      setProgress(0);
      await sendToBucket(ticket, file, { onProgress: setProgress });
      return completeVoiceUpload(teamId, brandId, ticket.voice_id, { name, mime });
    },
  });
  const save = async (file: File, typed: string) => {
    // Live's: the file name after its last dot (the whole name when it has none).
    const extension = file.name.split(".").pop();
    const name = extension === undefined || extension === "" ? typed : `${typed}.${extension}`;
    setProblem(null);
    try {
      const voice = await upload.mutateAsync({ file, name });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getListVoicesQueryKey(teamId, brandId) }),
        refreshTeamStorage(queryClient, teamId),
      ]);
      onSaved(voice);
      return true;
    } catch (error) {
      setProblem(`${typed}: ${toUserMessage(error)}`);
      return false;
    } finally {
      setProgress(null);
    }
  };
  return { save, pending: upload.isPending, progress, problem };
}
